import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { pole } from '../utils';
import { getTokenPayload, jeDispecerTechnik } from '../auth';
import { STAV_TIKET_LABEL, STAV_TIKET_COLOR, VYSLEDEK_LABEL } from '../tiketyConst';
import SlaIndikator from './SlaIndikator';
import PridatAktivitu from './PridatAktivitu';
import PodpisNaMiste from './PodpisNaMiste';
import VyberCesty from './VyberCesty';
import { urciPrioritu } from '../priorita';
import { DateTimeInput } from './DateInput';
import Modal from './Modal';
import {
  inputStyle, selectStyle, labelStyle, btnPrimary, btnOutline, btnDanger, card, chyboveHlaseni, apiChyba,
  formatDatCas, formatMin, formatHodiny, moznostiMin, naLocalInput, zLocalInput, otevriPdf, prazskyDen,
} from '../ui';

const API = process.env.REACT_APP_API_URL || '/api';
const DOBA_CESTY = moznostiMin(15, 720, 15);

function Radek({ k, children }) {
  return (
    <div style={{ display: 'flex', gap: '8px', padding: '4px 0', fontSize: '14px', flexWrap: 'wrap' }}>
      <span style={{ color: '#6b7280', minWidth: '150px' }}>{k}</span><span style={{ flex: 1 }}>{children}</span>
    </div>
  );
}

// ── Modální formuláře ────────────────────────────────────────────────────────

function ZmenitResitele({ tiket, onHotovo, onZavrit }) {
  const [resitele, setResitele] = useState([]);
  const [id, setId] = useState('');
  const [chyba, setChyba] = useState(null);
  useEffect(() => { axios.get(`${API}/resitele`).then(r => setResitele(pole(r.data))).catch(() => {}); }, []);
  const uloz = async () => {
    if (!id) { setChyba('Vyberte řešitele.'); return; }
    try { await axios.post(`${API}/tikety/${tiket.id}/priradit`, { resitel_id: Number(id) }); onHotovo(); }
    catch (e) { setChyba(apiChyba(e)); }
  };
  return (
    <Modal nadpis={tiket.resitel ? 'Změnit řešitele' : 'Přiřadit řešitele'} onZavrit={onZavrit}>
      <select style={selectStyle} value={id} onChange={e => setId(e.target.value)}>
        <option value="">— vyberte —</option>
        {resitele.filter(r => r.id !== tiket.resitel?.id).map(r => <option key={r.id} value={r.id}>{r.jmeno}</option>)}
      </select>
      {chyba && <div style={chyboveHlaseni}>{chyba}</div>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button style={{ ...btnPrimary, flex: 1 }} onClick={uloz}>Uložit</button>
        <button style={btnOutline} onClick={onZavrit}>Zrušit</button>
      </div>
    </Modal>
  );
}

function UpravitTiket({ tiket, onHotovo, onZavrit }) {
  const puvodniPrijato = naLocalInput(new Date(tiket.prijato_at));
  const [prodejny, setProdejny] = useState([]);
  const [priority, setPriority] = useState([]);
  const [pravidla, setPravidla] = useState([]);
  const [kriticka, setKriticka] = useState(tiket.priorita_kod === 'kriticka');
  const [kritickaZmenena, setKritickaZmenena] = useState(false);
  const [f, setF] = useState({
    obj: tiket.obj, provozovna_id: String(tiket.provozovna_id),
    prijato_at: puvodniPrijato, popis_problemu: tiket.popis_problemu,
    kontakt_jmeno: tiket.kontakt_jmeno, kontakt_telefon: tiket.kontakt_telefon,
  });
  const [chyba, setChyba] = useState(null);
  useEffect(() => {
    axios.get(`${API}/zakaznici`, { params: { pouze_aktivni: false } }).then(r => {
      const z = pole(r.data).find(x => x.id === tiket.zakaznik_id);
      setProdejny((z?.provozovny || []).filter(p => p.aktivni !== false || p.id === tiket.provozovna_id));
    }).catch(() => {});
    axios.get(`${API}/priority`).then(r => setPriority(pole(r.data))).catch(() => {});
    axios.get(`${API}/sla-pravidla`).then(r => setPravidla(pole(r.data))).catch(() => {});
  }, [tiket]);
  const pr = urciPrioritu(priority, pravidla, tiket.zakaznik_id);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const uloz = async () => {
    const telo = {};
    if (f.obj.trim() !== tiket.obj) telo.obj = f.obj.trim();
    if (Number(f.provozovna_id) !== tiket.provozovna_id) telo.provozovna_id = Number(f.provozovna_id);
    if (kritickaZmenena) {
      const cil = kriticka ? pr.kriticka : pr.bezna;
      if (cil && cil.id !== tiket.priorita_id) telo.priorita_id = cil.id;
    }
    if (f.prijato_at !== puvodniPrijato) telo.prijato_at = zLocalInput(f.prijato_at);
    if (f.popis_problemu.trim() !== tiket.popis_problemu) telo.popis_problemu = f.popis_problemu.trim();
    if (f.kontakt_jmeno.trim() !== tiket.kontakt_jmeno) telo.kontakt_jmeno = f.kontakt_jmeno.trim();
    if (f.kontakt_telefon.trim() !== tiket.kontakt_telefon) telo.kontakt_telefon = f.kontakt_telefon.trim();
    if (!Object.keys(telo).length) { onZavrit(); return; }
    try { await axios.put(`${API}/tikety/${tiket.id}`, telo); onHotovo(); }
    catch (e) { setChyba(apiChyba(e)); }
  };
  return (
    <Modal nadpis="Upravit údaje tiketu" onZavrit={onZavrit}>
      <label style={labelStyle}>OBJ</label><input style={inputStyle} value={f.obj} onChange={e => set('obj', e.target.value)} />
      <label style={labelStyle}>Prodejna</label>
      <select style={selectStyle} value={f.provozovna_id} onChange={e => set('provozovna_id', e.target.value)}>
        {prodejny.map(p => <option key={p.id} value={p.id}>{[p.nazev, p.mesto].filter(Boolean).join(' · ')}</option>)}
      </select>
      {(pr.zobrazit || tiket.priorita_kod === 'kriticka') && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '14px 0 0', padding: '12px', cursor: 'pointer',
          border: `2px solid ${kriticka ? '#dc2626' : '#d1d5db'}`, borderRadius: '8px', background: kriticka ? '#fef2f2' : '#fff', fontSize: '16px' }}>
          <input type="checkbox" checked={kriticka} onChange={e => { setKriticka(e.target.checked); setKritickaZmenena(true); }} style={{ width: '22px', height: '22px' }} />
          <span><strong>Kritická závada</strong><span style={{ display: 'block', fontSize: '12px', color: '#6b7280' }}>Změna priority přepočítá lhůty SLA podle aktuálního číselníku.</span></span>
        </label>
      )}
      <label style={labelStyle}>Čas přijetí</label>
      <DateTimeInput value={f.prijato_at} onChange={v => set('prijato_at', v)} />
      <label style={labelStyle}>Popis problému</label>
      <textarea style={{ ...inputStyle, minHeight: '80px' }} value={f.popis_problemu} onChange={e => set('popis_problemu', e.target.value)} />
      <label style={labelStyle}>Kontakt – jméno</label><input style={inputStyle} value={f.kontakt_jmeno} onChange={e => set('kontakt_jmeno', e.target.value)} />
      <label style={labelStyle}>Kontakt – telefon</label><input style={inputStyle} type="tel" value={f.kontakt_telefon} onChange={e => set('kontakt_telefon', e.target.value)} />
      {chyba && <div style={chyboveHlaseni}>{chyba}</div>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button style={{ ...btnPrimary, flex: 1 }} onClick={uloz}>Uložit</button>
        <button style={btnOutline} onClick={onZavrit}>Zrušit</button>
      </div>
    </Modal>
  );
}

function PridatPoznamku({ tiket, onHotovo, onZavrit }) {
  const [text, setText] = useState('');
  const [chyba, setChyba] = useState(null);
  const [ukladam, setUkladam] = useState(false);
  const uloz = async () => {
    if (!text.trim()) { setChyba('Napište poznámku.'); return; }
    setUkladam(true);
    try { await axios.post(`${API}/tikety/${tiket.id}/poznamky`, { text: text.trim() }); onHotovo(); }
    catch (e) { setChyba(apiChyba(e)); setUkladam(false); }
  };
  return (
    <Modal nadpis="Přidat poznámku" onZavrit={onZavrit}>
      <p style={{ fontSize: '14px', color: '#6b7280', marginTop: 0 }}>Krátký záznam k tiketu (např. „zákazník volal, přístup od 10:00“). Nepočítá se jako výjezd ani konzultace a na protokolu pro prodejnu nebude.{tiket.resitel ? ` Řešitel ${tiket.resitel.jmeno} se o ní dozví, pokud ji nepíše sám.` : ''}</p>
      <textarea style={{ ...inputStyle, minHeight: '90px' }} value={text} maxLength={2000} autoFocus onChange={e => setText(e.target.value)} />
      {chyba && <div style={chyboveHlaseni}>{chyba}</div>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button style={{ ...btnPrimary, flex: 1 }} onClick={uloz} disabled={ukladam}>{ukladam ? 'Ukládám…' : 'Uložit poznámku'}</button>
        <button style={btnOutline} onClick={onZavrit}>Zrušit</button>
      </div>
    </Modal>
  );
}

function Stornovat({ tiket, onHotovo, onZavrit }) {
  const [duvod, setDuvod] = useState('');
  const [chyba, setChyba] = useState(null);
  const uloz = async () => {
    if (!duvod.trim()) { setChyba('Důvod storna je povinný.'); return; }
    try { await axios.post(`${API}/tikety/${tiket.id}/storno`, { duvod: duvod.trim() }); onHotovo(); }
    catch (e) { setChyba(apiChyba(e)); }
  };
  return (
    <Modal nadpis={`Stornovat tiket ${tiket.obj}`} onZavrit={onZavrit}>
      <p style={{ fontSize: '14px', color: '#6b7280', marginTop: 0 }}>Jen pro chybně založené nebo duplicitní tikety. Stornovaný tiket se nepočítá do přehledů ani fakturace a nejde vrátit.</p>
      <label style={labelStyle}>Důvod storna *</label>
      <textarea style={{ ...inputStyle, minHeight: '80px' }} value={duvod} onChange={e => setDuvod(e.target.value)} />
      {chyba && <div style={chyboveHlaseni}>{chyba}</div>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button style={{ ...btnDanger, flex: 1 }} onClick={uloz}>Stornovat</button>
        <button style={btnOutline} onClick={onZavrit}>Zrušit</button>
      </div>
    </Modal>
  );
}

function DoplnitCestu({ aktivita, onHotovo, onZavrit }) {
  const oprava = aktivita.doba_cesty_min !== null && aktivita.doba_cesty_min !== undefined;
  const [pracoviste, setPracoviste] = useState([]);
  const [pracovisteId, setPracovisteId] = useState(aktivita.pracoviste_id ? String(aktivita.pracoviste_id) : '');
  const [doba, setDoba] = useState(oprava && !aktivita.cesta_zapoctena_u_tiketu_id ? String(aktivita.doba_cesty_min) : '');
  const [odkaz, setOdkaz] = useState(aktivita.cesta_zapoctena_u_tiketu_id ? String(aktivita.cesta_zapoctena_u_tiketu_id) : '');
  const [poznamka, setPoznamka] = useState(aktivita.poznamka || '');
  const [chyba, setChyba] = useState(null);
  useEffect(() => {
    axios.get(`${API}/pracoviste`).then(r => setPracoviste(pole(r.data))).catch(() => {});
    if (pracovisteId) return;
    axios.get(`${API}/technici`).then(r => {
      const me = pole(r.data).find(x => x.id === aktivita.vytvoril.id);
      if (me?.vychozi_pracoviste_id) setPracovisteId(String(me.vychozi_pracoviste_id));
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);
  const uloz = async () => {
    let telo;
    if (odkaz) {
      telo = { doba_cesty_min: 0, cesta_zapoctena_u_tiketu_id: Number(odkaz), poznamka: poznamka.trim() || null };
    } else {
      if (!pracovisteId || doba === '') { setChyba('Vyberte pracoviště a dobu cesty.'); return; }
      telo = { pracoviste_id: Number(pracovisteId), doba_cesty_min: Number(doba), poznamka: poznamka.trim() || null };
    }
    try { await axios.put(`${API}/aktivity/${aktivita.id}/cesta`, telo); onHotovo(); }
    catch (e) { setChyba(apiChyba(e)); }
  };
  return (
    <Modal nadpis={oprava ? 'Opravit cestu' : 'Doplnit cestu'} onZavrit={onZavrit}>
      {oprava && <p style={{ fontSize: '13px', color: '#6b7280', marginTop: 0 }}>Oprava se zapíše do historie tiketu.</p>}
      <label style={labelStyle}>Cesta započtena u jiného výjezdu ze stejného dne</label>
      <VyberCesty datum={prazskyDen(aktivita.prijezd_at)} technikId={aktivita.vytvoril.id}
        kromeTiketId={aktivita.tiket_id} value={odkaz} onChange={setOdkaz} />
      {!odkaz && (
        <>
          <label style={labelStyle}>Pracoviště</label>
          <select style={selectStyle} value={pracovisteId} onChange={e => setPracovisteId(e.target.value)}>
            <option value="">— vyberte —</option>
            {pracoviste.map(p => <option key={p.id} value={p.id}>{p.nazev}</option>)}
          </select>
          <label style={labelStyle}>Celková doba cesty (z pracoviště a zpět, celá trasa)</label>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Při více zastávkách v jednom dni zadejte celkový čas celé trasy u jednoho tiketu a u ostatních zvolte ‚Cesta započtena u jiného výjezdu‘.</div>
          <select style={selectStyle} value={doba} onChange={e => setDoba(e.target.value)}>
            <option value="">— vyberte —</option>
            {DOBA_CESTY.map(m => <option key={m} value={m}>{formatMin(m)}</option>)}
          </select>
        </>
      )}
      <label style={labelStyle}>Poznámka</label>
      <input style={inputStyle} value={poznamka} onChange={e => setPoznamka(e.target.value)} />
      {chyba && <div style={chyboveHlaseni}>{chyba}</div>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button style={{ ...btnPrimary, flex: 1 }} onClick={uloz}>Uložit</button>
        <button style={btnOutline} onClick={onZavrit}>Zrušit</button>
      </div>
    </Modal>
  );
}

function OpravitUzavreny({ aktivita, onHotovo, onZavrit }) {
  const podepsany = aktivita.protokoly.length > 0;
  const [reseni, setReseni] = useState(aktivita.reseni || '');
  const [zbozi, setZbozi] = useState(aktivita.zbozi.map(z => ({ polozka: z.polozka, mnozstvi: z.mnozstvi })));
  const [duvod, setDuvod] = useState('');
  const [chyba, setChyba] = useState(null);
  const uloz = async () => {
    if (!reseni.trim()) { setChyba('Popis řešení musí být vyplněný.'); return; }
    if (!duvod.trim()) { setChyba('Důvod opravy je povinný.'); return; }
    const telo = {
      duvod: duvod.trim(), reseni: reseni.trim(),
      zbozi: zbozi.filter(z => String(z.polozka).trim()).map(z => ({ polozka: String(z.polozka).trim(), mnozstvi: Number(String(z.mnozstvi).replace(',', '.')) || 1 })),
    };
    try { await axios.put(`${API}/aktivity/${aktivita.id}/oprava-po-uzavreni`, telo); onHotovo(); }
    catch (e) { setChyba(apiChyba(e)); }
  };
  return (
    <Modal nadpis={`Opravit uzavřený tiket – výjezd ${aktivita.cislo_protokolu}`} onZavrit={onZavrit}>
      <p style={{ fontSize: '13px', color: '#6b7280', marginTop: 0 }}>
        Tiket zůstane uzavřený. Oprava se zapíše do historie (kdo, kdy, původní a nová hodnota).
        {podepsany
          ? ' Původní podepsaný protokol zůstane beze změny a vznikne dodatek (nová verze protokolu) s vyznačením „doplněno po podpisu“. Dodatek není podepsaný.'
          : ' Výjezd není podepsaný, nová verze protokolu nevznikne.'}
      </p>
      <label style={labelStyle}>Provedené řešení</label>
      <textarea style={{ ...inputStyle, minHeight: '90px' }} value={reseni} onChange={e => setReseni(e.target.value)} />
      <label style={labelStyle}>Použité zboží</label>
      {zbozi.map((z, i) => (
        <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
          <input style={{ ...inputStyle, flex: 1 }} value={z.polozka} onChange={e => setZbozi(zb => zb.map((r, j) => (j === i ? { ...r, polozka: e.target.value } : r)))} />
          <input style={{ ...inputStyle, width: '76px' }} inputMode="decimal" value={z.mnozstvi} onChange={e => setZbozi(zb => zb.map((r, j) => (j === i ? { ...r, mnozstvi: e.target.value } : r)))} />
          <button type="button" style={{ ...btnOutline, padding: '0 14px' }} onClick={() => setZbozi(zb => zb.filter((_, j) => j !== i))} aria-label="Odebrat">×</button>
        </div>
      ))}
      <button type="button" style={btnOutline} onClick={() => setZbozi(zb => [...zb, { polozka: '', mnozstvi: 1 }])}>+ Přidat položku</button>
      <label style={labelStyle}>Důvod opravy *</label>
      <input style={inputStyle} value={duvod} onChange={e => setDuvod(e.target.value)} placeholder="např. technik zapomněl uvést HDMI kabel" />
      {chyba && <div style={chyboveHlaseni}>{chyba}</div>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button style={{ ...btnPrimary, flex: 1 }} onClick={uloz}>Uložit opravu</button>
        <button style={btnOutline} onClick={onZavrit}>Zrušit</button>
      </div>
    </Modal>
  );
}

function ZneplatnitProtokol({ aktivita, onHotovo, onZavrit }) {
  const [duvod, setDuvod] = useState('');
  const [chyba, setChyba] = useState(null);
  const uloz = async () => {
    if (!duvod.trim()) { setChyba('Důvod je povinný.'); return; }
    try { await axios.post(`${API}/aktivity/${aktivita.id}/protokol/zneplatnit`, { duvod: duvod.trim() }); onHotovo(); }
    catch (e) { setChyba(apiChyba(e)); }
  };
  return (
    <Modal nadpis={`Zneplatnit protokol ${aktivita.cislo_protokolu}`} onZavrit={onZavrit}>
      <p style={{ fontSize: '14px', color: '#6b7280', marginTop: 0 }}>
        Zneplatňuje se protokol s chybou v obsahu. PDF zůstane v archivu, podpis a jméno podepsaného zůstávají.
        Potom opravíte údaje výjezdu a vznikne nová verze protokolu s původním podpisem a poznámkou o opravě.
      </p>
      <label style={labelStyle}>Důvod *</label>
      <textarea style={{ ...inputStyle, minHeight: '80px' }} value={duvod} onChange={e => setDuvod(e.target.value)} />
      {chyba && <div style={chyboveHlaseni}>{chyba}</div>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button style={{ ...btnDanger, flex: 1 }} onClick={uloz}>Zneplatnit</button>
        <button style={btnOutline} onClick={onZavrit}>Zrušit</button>
      </div>
    </Modal>
  );
}

function OpravitVyjezd({ aktivita, onHotovo, onZavrit }) {
  const [resitele, setResitele] = useState([]);
  const [f, setF] = useState({
    prijezd_at: naLocalInput(new Date(aktivita.prijezd_at)), cas_na_miste_min: aktivita.cas_na_miste_min,
    popis_problemu: aktivita.popis_problemu || '', reseni: aktivita.reseni || '',
    dalsi_technik_id: aktivita.dalsi_technik ? String(aktivita.dalsi_technik.id) : '',
  });
  const [zbozi, setZbozi] = useState(aktivita.zbozi.map(z => ({ polozka: z.polozka, mnozstvi: z.mnozstvi })));
  const [chyba, setChyba] = useState(null);
  useEffect(() => { axios.get(`${API}/resitele`).then(r => setResitele(pole(r.data))).catch(() => {}); }, []);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const puvodniPrijezd = naLocalInput(new Date(aktivita.prijezd_at));
  const poslednhiVerze = Math.max(...aktivita.protokoly.map(p => p.verze)) + 1;
  const uloz = async () => {
    if (!f.reseni.trim() || !f.popis_problemu.trim()) { setChyba('Popis problému i řešení musí být vyplněné.'); return; }
    const telo = {
      cas_na_miste_min: Number(f.cas_na_miste_min),
      popis_problemu: f.popis_problemu.trim(), reseni: f.reseni.trim(),
      dalsi_technik_id: f.dalsi_technik_id ? Number(f.dalsi_technik_id) : null,
      zbozi: zbozi.filter(z => String(z.polozka).trim()).map(z => ({ polozka: String(z.polozka).trim(), mnozstvi: Number(String(z.mnozstvi).replace(',', '.')) || 1 })),
    };
    if (f.prijezd_at !== puvodniPrijezd) telo.prijezd_at = zLocalInput(f.prijezd_at);  // vteřiny původního času se zbytečně nepřepisují
    try { await axios.post(`${API}/aktivity/${aktivita.id}/protokol/opravit`, telo); onHotovo(); }
    catch (e) { setChyba(apiChyba(e)); }
  };
  return (
    <Modal nadpis={`Opravit výjezd – protokol ${aktivita.cislo_protokolu}, verze ${poslednhiVerze}`} onZavrit={onZavrit}>
      <div style={{ ...chyboveHlaseni, background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', marginTop: 0 }}>
        Důvod zneplatnění: {aktivita.protokoly[aktivita.protokoly.length - 1].zneplatneni_duvod}
      </div>
      <p style={{ fontSize: '13px', color: '#6b7280' }}>Podpis a jméno podepsaného zůstávají beze změny. Po uložení se vygeneruje verze {poslednhiVerze} protokolu s poznámkou o opravě a změna se zapíše do historie.</p>
      <label style={labelStyle}>Příjezd na prodejnu</label>
      <DateTimeInput value={f.prijezd_at} onChange={v => set('prijezd_at', v)} />
      <label style={labelStyle}>Čas na místě</label>
      <select style={selectStyle} value={f.cas_na_miste_min} onChange={e => set('cas_na_miste_min', e.target.value)}>
        {moznostiMin(30, 720, 30).map(m => <option key={m} value={m}>{formatMin(m)}</option>)}
      </select>
      <label style={labelStyle}>Popis problému</label>
      <textarea style={{ ...inputStyle, minHeight: '70px' }} value={f.popis_problemu} onChange={e => set('popis_problemu', e.target.value)} />
      <label style={labelStyle}>Provedené řešení</label>
      <textarea style={{ ...inputStyle, minHeight: '90px' }} value={f.reseni} onChange={e => set('reseni', e.target.value)} />
      <label style={labelStyle}>Použité zboží</label>
      {zbozi.map((z, i) => (
        <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
          <input style={{ ...inputStyle, flex: 1 }} value={z.polozka} onChange={e => setZbozi(zb => zb.map((r, j) => (j === i ? { ...r, polozka: e.target.value } : r)))} />
          <input style={{ ...inputStyle, width: '76px' }} inputMode="decimal" value={z.mnozstvi} onChange={e => setZbozi(zb => zb.map((r, j) => (j === i ? { ...r, mnozstvi: e.target.value } : r)))} />
          <button type="button" style={{ ...btnOutline, padding: '0 14px' }} onClick={() => setZbozi(zb => zb.filter((_, j) => j !== i))} aria-label="Odebrat">×</button>
        </div>
      ))}
      <button type="button" style={btnOutline} onClick={() => setZbozi(zb => [...zb, { polozka: '', mnozstvi: 1 }])}>+ Přidat položku</button>
      <label style={labelStyle}>Další technik</label>
      <select style={selectStyle} value={f.dalsi_technik_id} onChange={e => set('dalsi_technik_id', e.target.value)}>
        <option value="">— žádný —</option>
        {resitele.filter(r => r.id !== aktivita.vytvoril.id).map(r => <option key={r.id} value={r.id}>{r.jmeno}</option>)}
      </select>
      {chyba && <div style={chyboveHlaseni}>{chyba}</div>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button style={{ ...btnPrimary, flex: 1 }} onClick={uloz}>Uložit a vygenerovat verzi {poslednhiVerze}</button>
        <button style={btnOutline} onClick={onZavrit}>Zrušit</button>
      </div>
    </Modal>
  );
}

// ── Historie ─────────────────────────────────────────────────────────────────

const POLE_UPRAVY = { obj: 'OBJ', provozovna_id: 'prodejna', priorita_id: 'priorita', prijato_at: 'čas přijetí',
  popis_problemu: 'popis', kontakt_jmeno: 'kontakt', kontakt_telefon: 'telefon', reakce_h: 'lhůta reakce', vyreseni_h: 'lhůta vyřešení' };

const HODNOTY_UPRAVY = ['obj', 'popis_problemu', 'kontakt_jmeno', 'kontakt_telefon'];   // u nich historie ukazuje původní i novou hodnotu

const POLE_OPRAVY = { prijezd_at: 'příjezd', cas_na_miste_min: 'čas na místě', popis_problemu: 'popis problému', reseni: 'řešení', zbozi: 'zboží', dalsi_technik_id: 'další technik' };

function popisUdalosti(u, jmena) {
  const j = id => jmena[id] || (id ? `#${id}` : 'fronta');
  switch (u.typ) {
    case 'zalozeni': return `Tiket založen (${STAV_TIKET_LABEL[u.po?.stav] || ''}${u.po?.resitel_id ? `, řešitel ${j(u.po.resitel_id)}` : ', ve frontě'})`;
    case 'zmena_resitele': return `Změna řešitele: ${j(u.pred?.resitel_id)} → ${j(u.po?.resitel_id)}`;
    case 'zmena_stavu': return `Změna stavu: ${STAV_TIKET_LABEL[u.pred?.stav] || '—'} → ${STAV_TIKET_LABEL[u.po?.stav] || '—'}`;
    case 'storno': return `Stornováno – ${u.po?.duvod}`;
    case 'uprava_cesty': {
      const f = x => (x?.zapoctena_u_tiketu_id ? `započtena u tiketu č. ${x.zapoctena_u_tiketu_id}` : `${formatMin(x?.doba_cesty_min)}${x?.pracoviste ? ` (${x.pracoviste})` : ''}`);
      return `Oprava cesty u výjezdu ${u.po?.poradi}: ${f(u.pred)} → ${f(u.po)}`;
    }
    case 'oprava_vyjezdu': return `Opraven výjezd ${u.po?.poradi} (protokol verze ${u.po?.verze_protokolu}): ${Object.keys(u.po || {}).filter(k => !['aktivita_id', 'poradi', 'verze_protokolu', 'duvod'].includes(k)).map(k => POLE_OPRAVY[k] || k).join(', ') || 'bez změny údajů'}`;
    case 'oprava_uzavreneho': return `Opraven uzavřený tiket, výjezd ${u.po?.poradi}: ${Object.keys(u.po || {}).filter(k => !['aktivita_id', 'poradi', 'verze_protokolu', 'duvod'].includes(k)).map(k => POLE_OPRAVY[k] || k).join(', ')}${u.po?.verze_protokolu ? ` (dodatek k protokolu, verze ${u.po.verze_protokolu})` : ''} – ${u.po?.duvod}. Původně: ${Object.entries(u.pred || {}).filter(([k]) => ['reseni', 'zbozi'].includes(k)).map(([k, v]) => `${POLE_OPRAVY[k]}: ${Array.isArray(v) ? (v.join(', ') || 'žádné') : v}`).join('; ')}`;
    case 'uprava_pauzy': {
      const f = x => `${formatDatCas(x.od)} – ${x.do ? formatDatCas(x.do) : 'trvá'}`;
      return `Přepočtena pauza SLA (výjezd ${u.po?.poradi}): ${(u.pred?.pauzy || []).map(f).join('; ')} → ${(u.po?.pauzy || []).map(f).join('; ')}`;
    }
    case 'podpis_protokolu': return `Podepsán protokol výjezdu ${u.po?.poradi} (podepsal ${u.po?.podepsal_jmeno})`;
    case 'zneplatneni_protokolu': return `Zneplatněn protokol výjezdu ${u.po?.poradi}: ${u.po?.duvod}`;
    case 'uprava': return `Upraveno: ${Object.keys(u.po || {}).map(k => (HODNOTY_UPRAVY.includes(k) ? `${POLE_UPRAVY[k]} „${u.pred?.[k] ?? ''}“ → „${u.po[k]}“` : (POLE_UPRAVY[k] || k))).join('; ')}`;
    default: return u.typ;
  }
}

function KartaAktivity({ a, tiket, role, muzeUpravit, muzeOpravitCestu, onDoplnit, onPodepsat, onZneplatnit, onOpravit, onOpravitUzavreny, onPdfChyba }) {
  const otevri = (verze) => otevriPdf(axios, `${API}/aktivity/${a.id}/protokol`, verze ? { verze } : undefined).catch(e => onPdfChyba(e.message));
  const konz = a.typ === 'konzultace';
  return (
    <div style={{ ...card, marginBottom: '8px', borderLeft: `4px solid ${konz ? '#3b82f6' : '#3ab54a'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
        <strong>{konz ? '📞 Telefonická konzultace' : '🚗 Výjezd'} – {VYSLEDEK_LABEL[a.vysledek]}</strong>
        <span style={{ fontSize: '12px', color: '#6b7280' }}>{formatDatCas(a.created_at)} · Technik: {a.vytvoril.jmeno}{a.zapsal && a.zapsal.id !== a.vytvoril.id ? ` · zapsal: ${a.zapsal.jmeno}` : ''}</span>
      </div>
      {a.ceka_duvod && <div style={{ fontSize: '14px', color: '#6b7280' }}>Důvod čekání: {a.ceka_duvod}</div>}
      {konz ? (
        <div style={{ fontSize: '14px', marginTop: '6px' }}>
          <div>{formatDatCas(a.datum_cas)} · {formatMin(a.delka_min)}{a.s_kym ? ` · s kým: ${a.s_kym}` : ''}</div>
          {a.cena_text && <div>Cena: <strong>{a.cena_text}</strong></div>}
          <div style={{ whiteSpace: 'pre-wrap' }}>{a.popis_reseni}</div>
        </div>
      ) : (
        <div style={{ fontSize: '14px', marginTop: '6px' }}>
          <div style={{ color: '#6b7280' }}>Požadavek přijat: {formatDatCas(tiket.prijato_at)}</div>
          <div>Příjezd {formatDatCas(a.prijezd_at)} · na místě {formatMin(a.cas_na_miste_min)}{a.dalsi_technik ? ` · další technik: ${a.dalsi_technik.jmeno}` : ''}</div>
          <div style={{ whiteSpace: 'pre-wrap', marginTop: '4px' }}><span style={{ color: '#6b7280' }}>Řešení:</span> {a.reseni}</div>
          {a.zbozi.length > 0 && <div style={{ marginTop: '4px' }}><span style={{ color: '#6b7280' }}>Zboží:</span> {a.zbozi.map(z => `${z.polozka} ×${z.mnozstvi}`).join(', ')}</div>}
          <div style={{ marginTop: '4px' }}>
            <span style={{ color: '#6b7280' }}>Cesta:</span>{' '}
            {a.doba_cesty_min === null ? <em>nedoplněno</em>
              : a.cesta_zapoctena_u_tiketu_id ? `započtena u tiketu č. ${a.cesta_zapoctena_u_tiketu_id}${a.odkaz_obj ? ` (OBJ ${a.odkaz_obj})` : ''}`
              : `${formatMin(a.doba_cesty_min)}${a.pracoviste_nazev ? ` z pracoviště ${a.pracoviste_nazev}` : ''}`}
            {a.poznamka ? ` · ${a.poznamka}` : ''}
          </div>
          {a.odkaz_cesta_nevyplnena && (
            <div style={{ fontSize: '13px', color: '#92400e', background: '#fef3c7', borderRadius: '6px', padding: '6px 10px', marginTop: '6px' }}>
              Cesta u tiketu č. {a.cesta_zapoctena_u_tiketu_id}{a.odkaz_obj ? ` (OBJ ${a.odkaz_obj})` : ''} ještě není vyplněná, takže se zatím nezapočítá nikde.
            </div>
          )}
          <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            <span style={{ background: '#f3f4f6', borderRadius: '6px', padding: '3px 8px', fontSize: '13px' }}>
              Protokol {a.cislo_protokolu}{a.podepsano ? ` · podepsal ${a.podepsal_jmeno}, ${formatDatCas(a.podepsano_at)}` : ' · nepodepsáno'}
            </span>
            {!a.podepsano && <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#fff', background: '#dc2626', borderRadius: '10px', padding: '3px 10px' }}>Nepodepsáno</span>}
            {a.protokol_platny && !(a.protokoly.some(p => p.dodatek) && role !== 'technik') && <button style={{ ...btnPrimary, padding: '6px 12px', minHeight: 0, fontSize: '14px' }} onClick={() => otevri()}>📄 Protokol (PDF)</button>}
            {tiket.stav === 'uzavreno' && (role === 'admin' || role === 'dispecer') && !a.ceka_na_opravu && <button style={{ ...btnOutline, padding: '6px 12px', minHeight: 0 }} onClick={() => onOpravitUzavreny(a)}>Opravit zboží a řešení</button>}
            {a.ceka_na_opravu && <span style={{ fontSize: '12px', color: '#b91c1c', background: '#fee2e2', borderRadius: '10px', padding: '2px 8px' }}>Protokol zneplatněn – čeká na opravu</span>}
            {role === 'admin' && a.ceka_na_opravu && <button style={{ ...btnPrimary, padding: '6px 12px', minHeight: 0, fontSize: '14px' }} onClick={() => onOpravit(a)}>Opravit výjezd a vygenerovat novou verzi</button>}
            {role === 'admin' && a.protokol_platny && !a.protokoly.some(p => p.dodatek) && <button style={{ ...btnOutline, padding: '6px 12px', minHeight: 0, color: '#b91c1c' }} onClick={() => onZneplatnit(a)}>Zneplatnit protokol</button>}
            {a.nedoplneno && <span style={{ fontSize: '12px', color: '#9a3412', background: '#ffedd5', borderRadius: '10px', padding: '2px 8px' }}>Nedoplněno</span>}
            {muzeUpravit && a.nedoplneno && <button style={{ ...btnOutline, padding: '6px 12px', minHeight: 0 }} onClick={() => onDoplnit(a)}>Doplnit cestu</button>}
            {muzeUpravit && muzeOpravitCestu && !a.nedoplneno && <button style={{ ...btnOutline, padding: '6px 12px', minHeight: 0 }} onClick={() => onDoplnit(a)}>Opravit cestu</button>}
            {muzeUpravit && !a.podepsano && <button style={{ ...btnPrimary, padding: '6px 12px', minHeight: 0, fontSize: '14px' }} onClick={() => onPodepsat(a)}>✍️ Podepsat na místě</button>}
          </div>
          {role !== 'technik' && a.protokoly.some(p => p.dodatek) && a.protokoly.filter(p => p.platny).map(p => (
            <div key={`v${p.verze}`} style={{ fontSize: '13px', marginTop: '4px', padding: '6px 10px', borderRadius: '6px',
              background: p.dodatek ? '#fff7ed' : '#f0fdf4', border: `1px solid ${p.dodatek ? '#fed7aa' : '#bbf7d0'}` }}>
              {p.dodatek
                ? <><strong>Verze {p.verze} – dodatek po podpisu</strong> ({formatDatCas(p.vygenerovano_at)}, {p.dodatek_kdo}; důvod: {p.dodatek_duvod}) · <em>nepodepsáno</em></>
                : <><strong>Verze {p.verze} – podepsaná původní</strong> (podepsal {a.podepsal_jmeno}, {formatDatCas(a.podepsano_at)})</>}{' '}
              <button style={{ background: 'none', border: 'none', color: '#1d4ed8', textDecoration: 'underline', cursor: 'pointer', fontSize: '13px', padding: 0 }} onClick={() => otevri(p.verze)}>📄 PDF</button>
            </div>
          ))}
          {role === 'admin' && a.protokoly.filter(p => !p.platny).map(p => (
            <div key={p.verze} style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              Zneplatněn protokol verze {p.verze} ({formatDatCas(p.zneplatnen_at)}): {p.zneplatneni_duvod}{' '}
              <button style={{ background: 'none', border: 'none', color: '#1d4ed8', textDecoration: 'underline', cursor: 'pointer', fontSize: '12px', padding: 0 }} onClick={() => otevri(p.verze)}>PDF</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Detail ───────────────────────────────────────────────────────────────────

export default function DetailTiketu({ tiketId, naviguj, role }) {
  const ja = getTokenPayload();
  const [t, setT] = useState(null);
  const [chyba, setChyba] = useState(null);
  const [jmena, setJmena] = useState({});
  const [formular, setFormular] = useState(false);
  const [menu, setMenu] = useState(false);
  const [pdfChyba, setChyba2] = useState(null);
  const [modal, setModal] = useState(null); // { typ, aktivita? }

  const nacti = useCallback(() => {
    axios.get(`${API}/tikety/${tiketId}`).then(r => { setT(r.data); setChyba(null); })
      .catch(e => setChyba(apiChyba(e, 'Tiket se nepodařilo načíst.')));
  }, [tiketId]);
  useEffect(() => { nacti(); }, [nacti]);
  useEffect(() => {
    axios.get(`${API}/technici`).then(r => setJmena(Object.fromEntries(pole(r.data).map(x => [x.id, x.jmeno])))).catch(() => {});
  }, []);

  const zpet = () => naviguj(role === 'technik' ? 'moje-tikety' : 'aktivni-tikety');
  const [upozorneni, setUpozorneni] = useState(null);
  // vyjezd = právě uložený výjezd, který se má hned podepsat na místě (jinak undefined/null)
  const hotovo = (vyjezd) => {
    setModal(vyjezd?.id ? { typ: 'podpis', aktivita: vyjezd } : null);
    setFormular(false); setMenu(false); setUpozorneni(null); nacti();
  };
  const podpisZavren = () => {
    setModal(null);
    setUpozorneni('Výjezd je uložen, ale protokol není podepsán. Podepsat ho můžete tlačítkem „Podepsat na místě“ u výjezdu.');
    nacti();
  };

  if (chyba) return <div><button style={btnOutline} onClick={zpet}>← Zpět</button><div style={chyboveHlaseni}>{chyba}</div></div>;
  if (!t) return <p>Načítám…</p>;

  const spravce = role === 'dispecer' || role === 'admin';
  const aktivni = ['novy', 'prirazeno', 'v_reseni', 'ceka'].includes(t.stav);
  const vzitSi = async () => {
    try { await axios.post(`${API}/tikety/${t.id}/priradit`, { resitel_id: ja.uid }); nacti(); }
    catch (e) { setChyba(apiChyba(e)); }
  };

  if (formular) {
    return (
      <div>
        <button style={{ ...btnOutline, marginBottom: '12px' }} onClick={() => setFormular(false)}>← Zpět na tiket</button>
        <div style={{ color: '#6b7280', marginBottom: '8px' }}>OBJ {t.obj} · {t.zakaznik_nazev}, {t.provozovna_nazev}</div>
        <PridatAktivitu tiket={t} onHotovo={hotovo} onZrusit={() => setFormular(false)} onPrirazeno={nacti} />
      </div>
    );
  }

  const sla = t.sla;
  const historie = [
    ...t.aktivity.map(a => ({ druh: 'aktivita', cas: a.created_at, id: `a${a.id}`, a })),
    ...t.udalosti.map(u => ({ druh: 'udalost', cas: u.created_at, id: `u${u.id}`, u })),
    ...t.poznamky.map(p => ({ druh: 'poznamka', cas: p.created_at, id: `p${p.id}`, p })),
  ].sort((x, y) => (x.cas < y.cas ? 1 : x.cas > y.cas ? -1 : 0));

  const polozkaMenu = (text, akce, nebezpecne) => (
    <button key={text} onClick={() => { setMenu(false); akce(); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '14px 16px', border: 'none', background: '#fff', fontSize: '15px', cursor: 'pointer', color: nebezpecne ? '#b91c1c' : '#333', borderBottom: '1px solid #f3f4f6' }}>{text}</button>
  );

  return (
    <div>
      <button style={{ ...btnOutline, marginBottom: '12px' }} onClick={zpet}>← Zpět</button>

      {/* Hlavička */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, color: '#333' }}>OBJ {t.obj}</h2>
            <div style={{ color: '#6b7280', fontSize: '13px' }}>tiket č. {t.id}{t.navazuje_na_tiket_id && (
              <> · navazuje na <button onClick={() => naviguj('tiket-detail', t.navazuje_na_tiket_id)} style={{ background: 'none', border: 'none', color: '#1d4ed8', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: '13px' }}>tiket č. {t.navazuje_na_tiket_id}</button></>
            )}{t.navazujici?.length > 0 && (
              <> · navazující: {t.navazujici.map((n, i) => (
                <React.Fragment key={n.id}>{i > 0 && ', '}<button onClick={() => naviguj('tiket-detail', n.id)} style={{ background: 'none', border: 'none', color: '#1d4ed8', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: '13px' }}>tiket č. {n.id}</button></React.Fragment>
              ))}</>
            )}</div>
          </div>
          {spravce && (
            <div style={{ position: 'relative' }}>
              <button style={btnOutline} onClick={() => setMenu(m => !m)} aria-label="Další akce">⋯</button>
              {menu && (
                <div style={{ position: 'absolute', right: 0, top: '50px', zIndex: 50, background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: '230px' }}>
                  {aktivni && polozkaMenu('Změnit řešitele', () => setModal({ typ: 'resitel' }))}
                  {aktivni && polozkaMenu('Upravit údaje tiketu', () => setModal({ typ: 'uprava' }))}
                  {t.stav === 'uzavreno' && polozkaMenu('Založit navazující tiket', () => naviguj('novy-tiket', t.id))}
                  {polozkaMenu('Interní souhrn (PDF)', () => otevriPdf(axios, `${API}/tikety/${t.id}/souhrn.pdf`).catch(e => setChyba2(e.message)))}
                  {role === 'admin' && aktivni && polozkaMenu('Stornovat', () => setModal({ typ: 'storno' }), true)}
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ marginTop: '8px', fontSize: '15px' }}>{t.zakaznik_nazev} · <strong>{t.provozovna_nazev}</strong>{t.provozovna_mesto ? `, ${t.provozovna_mesto}` : ''}</div>
        {t.provozovna_adresa && <div style={{ fontSize: '13px', color: '#6b7280' }}>{t.provozovna_adresa}</div>}
        <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          <span style={{ color: '#fff', background: STAV_TIKET_COLOR[t.stav], borderRadius: '12px', padding: '3px 12px', fontSize: '13px', fontWeight: 'bold' }}>{STAV_TIKET_LABEL[t.stav]}</span>
          {t.bude_vyjezd && <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: '12px', padding: '3px 10px', fontSize: '13px' }}>bude výjezd</span>}
          {t.nedoplneno && <span style={{ background: '#ffedd5', color: '#9a3412', borderRadius: '12px', padding: '3px 10px', fontSize: '13px' }}>Nedoplněno</span>}
          <span style={{ fontSize: '14px' }}>Řešitel: <strong>{t.resitel?.jmeno || 've frontě'}</strong></span>
          <span style={{ fontSize: '14px', color: '#6b7280' }}>Založil: {t.zalozil.jmeno}</span>
        </div>
        {t.stav === 'stornovano' && <div style={{ ...chyboveHlaseni }}>Stornováno {formatDatCas(t.storno_at)} ({t.storno_by?.jmeno}): {t.storno_duvod}</div>}
        {aktivni && (role === 'technik' || jeDispecerTechnik()) && !t.resitel && (
          <button style={{ ...btnPrimary, width: '100%', marginTop: '12px' }} onClick={vzitSi}>Vzít si</button>
        )}
        {aktivni && spravce && !t.resitel && (
          <button style={{ ...btnPrimary, width: '100%', marginTop: '12px' }} onClick={() => setModal({ typ: 'resitel' })}>Přiřadit řešitele</button>
        )}
        {(t.muze_pridat_aktivitu || t.muze_pridat_poznamku || (aktivni && spravce)) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
            {t.muze_pridat_aktivitu && (
              <button style={{ ...btnPrimary, flex: '1 1 160px' }} onClick={() => setFormular(true)}>Přidat aktivitu</button>
            )}
            {t.muze_pridat_poznamku && (
              <button style={{ ...btnOutline, flex: '1 1 160px' }} onClick={() => setModal({ typ: 'poznamka' })}>📝 Přidat poznámku</button>
            )}
            {aktivni && spravce && (
              <button style={{ ...btnOutline, flex: '1 1 160px' }} onClick={() => setModal({ typ: 'uprava' })}>✏️ Upravit tiket</button>
            )}
          </div>
        )}
      </div>

      {/* Poznámky (nejnovější první) */}
      {t.poznamky.length > 0 && (
        <div style={{ ...card, background: '#fffbeb', border: '1px solid #fcd34d' }}>
          <strong>Poznámky</strong>
          {t.poznamky.map(p => (
            <div key={p.id} style={{ padding: '8px 0', borderTop: '1px solid #fde68a', marginTop: '8px' }}>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '15px' }}>{p.text}</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{p.autor.jmeno} · {formatDatCas(p.created_at)}</div>
            </div>
          ))}
        </div>
      )}

      {/* SLA */}
      {sla && sla.indikator && (
        <div style={card}>
          <strong>SLA</strong> <span style={{ marginLeft: '8px' }}><SlaIndikator sla={sla} /></span>
          <Radek k="Přijato">{formatDatCas(t.prijato_at)}</Radek>
          <Radek k="Start lhůty">{formatDatCas(sla.start)}</Radek>
          {sla.termin_reakce && <Radek k="Termín reakce">{formatDatCas(sla.termin_reakce)} {sla.reakce_splnena === true && <span style={{ color: '#16a34a' }}>✓ splněno</span>}{sla.reakce_splnena === false && <span style={{ color: '#dc2626' }}>✗ nesplněno</span>}</Radek>}
          {sla.termin_vyreseni && <Radek k="Termín vyřešení">{formatDatCas(sla.termin_vyreseni)}</Radek>}
          {sla.zbyva_h !== null && t.stav !== 'uzavreno' && <Radek k={sla.zbyva_h < 0 ? 'Překročeno o' : 'Zbývá'}>{formatHodiny(Math.abs(sla.zbyva_h))}</Radek>}
          {sla.vyreseni_splneno !== null && <Radek k="Vyřešení">{sla.vyreseni_splneno ? <span style={{ color: '#16a34a' }}>✓ splněno</span> : <span style={{ color: '#dc2626' }}>✗ nesplněno</span>}</Radek>}
          <Radek k="Doba pozastavení">{formatHodiny(sla.pozastaveno_h)}{sla.pozastaveno && ' (právě běží)'}</Radek>
          {t.pauzy.map((p, i) => <div key={i} style={{ fontSize: '12px', color: '#6b7280', paddingLeft: '158px' }}>{formatDatCas(p.od)} – {p.do ? formatDatCas(p.do) : 'trvá'}: {p.duvod}</div>)}
        </div>
      )}

      {/* Popis a kontakt */}
      <div style={card}>
        <strong>Popis problému</strong>
        <div style={{ whiteSpace: 'pre-wrap', margin: '6px 0 10px' }}>{t.popis_problemu}</div>
        <Radek k="Kontakt">{t.kontakt_jmeno} · <a href={`tel:${t.kontakt_telefon.replace(/\s/g, '')}`} style={{ color: '#1d4ed8' }}>{t.kontakt_telefon}</a></Radek>
        {t.priorita_zobrazit && <Radek k="Priorita">{t.priorita_nazev}</Radek>}
        {t.stav === 'uzavreno' && <Radek k="Uzavřeno">{formatDatCas(t.uzavreno_at)} ({t.uzavrel?.jmeno})</Radek>}
      </div>

      {/* Historie */}
      <h3 style={{ margin: '18px 0 8px' }}>Historie</h3>
      {pdfChyba && <div style={chyboveHlaseni}>{pdfChyba}</div>}
      {upozorneni && <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', color: '#92400e', borderRadius: '8px', padding: '10px 12px', margin: '10px 0', fontSize: '15px' }}>{upozorneni}</div>}
      {historie.map(h => h.druh === 'aktivita' ? (
        <KartaAktivity key={h.id} a={h.a} tiket={t} role={role} onPdfChyba={setChyba2}
          onZneplatnit={a => setModal({ typ: 'zneplatnit', aktivita: a })}
          onOpravit={a => setModal({ typ: 'oprava', aktivita: a })}
          onOpravitUzavreny={a => setModal({ typ: 'oprava_uzavreneho', aktivita: a })}
          muzeUpravit={spravce || t.resitel?.id === ja?.uid || h.a.vytvoril.id === ja?.uid}
          muzeOpravitCestu={role === 'admin' || (role === 'technik' && aktivni)}
          onDoplnit={a => setModal({ typ: 'cesta', aktivita: a })} onPodepsat={a => setModal({ typ: 'podpis', aktivita: a })} />
      ) : h.druh === 'poznamka' ? (
        <div key={h.id} style={{ fontSize: '13px', color: '#374151', padding: '4px 4px 4px 12px', borderLeft: '2px solid #f59e0b', marginBottom: '8px', whiteSpace: 'pre-wrap' }}>
          {formatDatCas(h.cas)} · {h.p.autor.jmeno} – 📝 Poznámka: {h.p.text}
        </div>
      ) : (
        <div key={h.id} style={{ fontSize: '13px', color: '#6b7280', padding: '4px 4px 4px 12px', borderLeft: '2px solid #d1d5db', marginBottom: '8px' }}>
          {formatDatCas(h.cas)} · {h.u.uzivatel.jmeno} – {popisUdalosti(h.u, jmena)}
        </div>
      ))}

      {/* Součty */}
      <div style={{ ...card, marginTop: '18px' }}>
        <strong>Součty</strong>
        <Radek k="Počet výjezdů">{t.souhrn.pocet_vyjezdu}</Radek>
        <Radek k="Počet konzultací">{t.souhrn.pocet_konzultaci}</Radek>
        <Radek k="Čas na místě">{formatMin(t.souhrn.cas_na_miste_min)}</Radek>
        <Radek k="Čas na cestě">{formatMin(t.souhrn.cas_na_ceste_min)}</Radek>
        {t.souhrn.po_technicich.length > 1 && t.souhrn.po_technicich.map(x => (
          <div key={x.jmeno} style={{ fontSize: '12px', color: '#6b7280', paddingLeft: '158px' }}>{x.jmeno}: na místě {formatMin(x.na_miste_min)}, cesta {formatMin(x.na_ceste_min)}</div>
        ))}
        <Radek k="Zboží">{t.souhrn.zbozi.length ? t.souhrn.zbozi.map(z => `${z.polozka} ×${z.mnozstvi}`).join(', ') : '—'}</Radek>
      </div>

      {modal?.typ === 'resitel' && <ZmenitResitele tiket={t} onHotovo={hotovo} onZavrit={() => setModal(null)} />}
      {modal?.typ === 'uprava' && <UpravitTiket tiket={t} onHotovo={hotovo} onZavrit={() => setModal(null)} />}
      {modal?.typ === 'poznamka' && <PridatPoznamku tiket={t} onHotovo={hotovo} onZavrit={() => setModal(null)} />}
      {modal?.typ === 'storno' && <Stornovat tiket={t} onHotovo={hotovo} onZavrit={() => setModal(null)} />}
      {modal?.typ === 'cesta' && <DoplnitCestu aktivita={modal.aktivita} onHotovo={hotovo} onZavrit={() => setModal(null)} />}
      {modal?.typ === 'oprava' && <OpravitVyjezd aktivita={modal.aktivita} onHotovo={hotovo} onZavrit={() => setModal(null)} />}
      {modal?.typ === 'oprava_uzavreneho' && <OpravitUzavreny aktivita={modal.aktivita} onHotovo={hotovo} onZavrit={() => setModal(null)} />}
      {modal?.typ === 'zneplatnit' && <ZneplatnitProtokol aktivita={modal.aktivita} onHotovo={hotovo} onZavrit={() => setModal(null)} />}
      {modal?.typ === 'podpis' && <PodpisNaMiste aktivita={modal.aktivita} onHotovo={() => hotovo()} onZavrit={podpisZavren} />}
    </div>
  );
}
